import { memo, useMemo, useState } from "react";
import styles from "./OrderDetailsSideDialog.module.scss";
import CommonSideDialog from "../../../../common/components/side-dialog/CommonSideDialog";
import TabSelector, { Tab } from "../../../../common/components/tab-selector/TabSelector";
import { SortIcon, CloseCircleIcon, ToolsIcon } from "../../../../assets/icons";
import { toast } from "react-toastify";
import ordersService from "../../../../httpService/OrdersService";
import { OrderStatus, allowedTransitionOrderStates } from "../../../../model/orders/orders";
import SelectOrderStatus, { SelectOrderStatusItem } from "../../../../common/components/select-order-status/SelectOrderStatus";
import { BounceLoader } from "react-spinners";
import { formatDate } from "../../../../utils";
import { ProviderCustomerOrder } from "../../../../model/customer/customer";
import { BuildingTypeImages } from "../../../../model/building-type";

type Props = {
	order: ProviderCustomerOrder;
	onCloseDialog: () => void;
	onUpdateOrderStatus: (orderId: string, newStatus: OrderStatus) => void;
	isReduxQuery?: boolean;
};

const OrderDetailsSideDialog = memo((props: Props) => {
	const { onCloseDialog, order } = props;

	const [selectedTab, setSelectedTab] = useState(1);
	const [status, setStatus] = useState<string>("");
	const [loading, setLoading] = useState(false);

	const tabs : Tab[] = [
		{ 
			id: 1, 
			title: "Overview", onClick: () => setSelectedTab(1),
			icon: <SortIcon /> 
		},
		{ 
			id: 2, title: "Installation", 
			onClick: () => setSelectedTab(2),
			icon: <ToolsIcon height={20} width={20} />  
		},
	];

	const statuses = useMemo<SelectOrderStatusItem[]>(() => {
		const allowedTransitionStates = allowedTransitionOrderStates(order.status);

		return [
			new SelectOrderStatusItem("In Progress", "Order Received", !allowedTransitionStates.includes("In Progress")),
			new SelectOrderStatusItem("Procesed", "Order being Processed", !allowedTransitionStates.includes("Procesed")),
			new SelectOrderStatusItem("Delivery In Progress", "Delivery in Progress", !allowedTransitionStates.includes("Delivery In Progress")),
			new SelectOrderStatusItem("Delivered", "Delivered", !allowedTransitionStates.includes("Delivered")),
			new SelectOrderStatusItem("Installed", "Installed", !allowedTransitionStates.includes("Installed")),
			new SelectOrderStatusItem("Retrieved", "Retrieved", !allowedTransitionStates.includes("Retrieved")),
			new SelectOrderStatusItem("Under Maintenance", "Under Maintenance", !allowedTransitionStates.includes("Under Maintenance")),
		];
	}, [order]);

	const imageForBuildingType = (buildingType: string) => {
		switch (buildingType) {
			case "Story Building":
				return BuildingTypeImages.storyBuildingImage;
			case "Bungalow":
				return BuildingTypeImages.bungalowImage;
			default:
				return BuildingTypeImages.storyBuildingImage;
		}
	};

	const saveNewOrderStatus = async () => {
		if(props.isReduxQuery) {
			props.onUpdateOrderStatus(order.id, status as OrderStatus);
			return;
		}

		try {
			setLoading(true);
			const obj = {
				orderId: props.order.id,
				newStatus: status,
			};

			await ordersService.setNewOrderStatus(obj);

			toast.success("Order status updated successfully.");
			props.onUpdateOrderStatus(props.order.id, status as OrderStatus);
		} catch (error: any) {
			toast.error(error.response.data.title || "Something went wrong. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<CommonSideDialog onCloseDialog={onCloseDialog}>
			<div className={styles.contentWrapper}>
				<div className={styles.header}>
					<div className={styles.closeButton} onClick={onCloseDialog}>
						<CloseCircleIcon />
					</div>
					<h2>Order Details</h2>
					<TabSelector tabs={tabs} selectedTab={selectedTab}/>
				</div>

				{selectedTab === 1 && (
					<div>
						<div className={styles.tabContent}>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Status</div>
								<div className={styles.itemSelect}>
									<SelectOrderStatus
										items={statuses}
										placeholder="Select Status"
										onChange={item => {
											console.log(item);
											setStatus(item?.key);
										}}
									/>
								</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Order Id</div>
								<div className={styles.itemValue}>{order.id}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Customer Name</div>
								<div className={styles.itemValue}>{order.customerName}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Customer Phone Number</div>
								<div className={styles.itemValue}>{order?.customerPhoneNumber}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Customer Address</div>
								<div className={styles.itemValue}>{order?.customerAddress}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Product Ordered</div>
								<div className={styles.itemValue}>{order.productName}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Payment Date</div>
								<div className={styles.itemValue}>{formatDate(order.paymentDate ?? "")}</div>
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Order Status</div>
								<div className={styles.itemValue}>{order.status}</div>
							</div>
						</div>

						<div className={styles.btnWrapper}>
							<button className={`standardButton`} disabled={loading} onClick={saveNewOrderStatus}>
								{loading ? <BounceLoader color="#DE841B" size={25} /> : "Save"}
							</button>
						</div>
					</div>
				)}

				{selectedTab === 2 && (
					<div>
						<div className={styles.tabContent}>
							<div className={styles.imagesContainer}>
								<div className={styles.imageContainer}>
									<img className={styles.image} src={imageForBuildingType(order.buildingType)} alt="Building Type" />
									<div className={styles.imageTitle}>{order.buildingType}</div>
								</div>
								{order.roofTypeImage && (
									<div className={styles.imageContainer}>
										<img className={styles.image} src={order.roofTypeImage} alt="Roof Type" />
										<div className={styles.imageTitle}>{order.roofType}</div>
									</div>
								)}
							</div>
							<div className={styles.item}>
								<div className={styles.itemTitle}>Installation Address</div>
								<div className={styles.itemValue}>{order?.customerAddress}</div>
							</div>
							{!!order.numberOfFloors && (
								<div className={styles.item}>
									<div className={styles.itemTitle}>Number of Floors</div>
									<div className={styles.itemValue}>{order?.numberOfFloors}</div>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</CommonSideDialog>
	);
});

export default OrderDetailsSideDialog;
